/**
 * `@` task mentions in the chat composer (ADR-0067): where one is being typed, which of the sender's
 * tasks the picker offers, and the literal `@<task-id>` a choice leaves in the message for get_task.
 *
 * Kept JSX-free so `node --test` can load it directly.
 */
import type { TaskRecord } from "../types";
import { isActiveTask } from "../../../shared/wire/tasks.ts";

/** An `@` typed at the caret: where it sits and what follows it so far. */
export interface Mention {
	start: number;
	query: string;
}

/** How many tasks the picker lists at once; typing narrows it. */
export const MENTION_CHOICES = 8;

/**
 * The mention the caret is typing, if any. The `@` (or a Chinese keyboard's `＠`) starts the text or
 * follows a character no email address or path ends in, and nothing between it and the caret is whitespace.
 */
export function mentionAt(text: string, caret: number): Mention | null {
	const match = /(?:^|[^\w.+\-/])[@＠]([^\s@＠]*)$/.exec(text.slice(0, caret));
	if (!match) return null;
	const query = match[1]!;
	return { start: caret - query.length - 1, query };
}

/** The sender's active tasks, todo and in-progress ones and undeployed development tickets, whose title contains the query, ignoring case, in the order given. */
export function mentionChoices(tasks: TaskRecord[], query: string): TaskRecord[] {
	const q = query.toLowerCase();
	return tasks.filter((t) => isActiveTask(t) && t.title.toLowerCase().includes(q)).slice(0, MENTION_CHOICES);
}

/** Replaces the typed mention with the task's literal `@<id>` and a space, and puts the caret after the space. */
export function insertMention(text: string, mention: Mention, id: string): { text: string; caret: number } {
	const rest = text.slice(mention.start + 1 + mention.query.length);
	const token = `@${id}`;
	return {
		text: text.slice(0, mention.start) + token + (rest.startsWith(" ") ? rest : ` ${rest}`),
		caret: mention.start + token.length + 1,
	};
}
